import { create } from 'zustand'
import { FEATURES } from '../lib/config/constants.js'
import { api } from '../lib/api.js'

/**
 * Validates that a feature name exists in the FEATURES constant
 * @param {Object} feature - Feature object from FEATURES constant
 * @throws {Error} If feature is not found in FEATURES
 */
const validateFeatureName = (feature) => {
  const validFeatureNames = Object.keys(FEATURES).map((k) => FEATURES[k])

  if (!validFeatureNames.includes(feature)) {
    throw new Error(
      `Unrecognized feature name '${feature}': Make sure you are using the FEATURES object and that it contains a key for the feature you are checking.`,
    )
  }
}

const initialState = {
  theme: 'modalview',
  features: {},
  privacyPolicyUrl: null,
  termsConditionsUrl: null,
  publicLoaded: false,
  authedLoaded: false,
  loadError: null,
}

/**
 * Tenant Store - Manages tenant configuration and feature flags
 *
 * Tracks two resolution flags so consumers can distinguish "no config response yet"
 * (defaults still in effect) from "config has arrived":
 *   - publicLoaded: rpc-public/tenant.getConfig has resolved
 *   - authedLoaded: rpc/tenant.getConfig has resolved
 *
 * Either flag being true means the `intercom` flag is trustworthy.
 */
export const useTenantStore = create((set, get) => ({
  ...initialState,

  // Actions
  setLoadError: (error) => set({ loadError: error }),

  reset: () => set({ ...initialState }),

  setTenant: (config) =>
    set({
      theme: config.theme,
      features: config.features || {},
      privacyPolicyUrl: config.privacyPolicyUrl,
      termsConditionsUrl: config.termsConditionsUrl,
      authedLoaded: true,
    }),

  /**
   * Fetches tenant configuration from the public endpoint (no auth required).
   * Called on app boot before authentication to load theme, features, and policy URLs.
   * Mirrors legacy MobX Tenant.getPublicConfig() behavior.
   */
  fetchPublicConfig: async () => {
    try {
      const config = await api.getTenantPublicConfig()
      set({
        theme: config.theme,
        features: config.features || {},
        privacyPolicyUrl: config.privacyPolicyUrl,
        termsConditionsUrl: config.termsConditionsUrl,
        publicLoaded: true,
      })
      return config
    } catch (error) {
      console.error('tenantStore: Failed to fetch public config', error)
      set({ loadError: error })
      throw error
    }
  },

  fetchConfig: async () => {
    try {
      const config = await api.getTenantConfig()
      set({
        theme: config.theme,
        features: config.features || {},
        privacyPolicyUrl: config.privacyPolicyUrl,
        termsConditionsUrl: config.termsConditionsUrl,
        authedLoaded: true,
      })
      return config
    } catch (error) {
      console.error('Zustand tenantStore: Failed to fetch config', error)
      set({ loadError: error })
      throw error
    }
  },

  /**
   * Returns true if this tenant has `feature` turned on, and false otherwise.
   * Features are assumed to be on unless explicitly disabled in the database for the tenant.
   *
   * Logic copied from src/app/models/Tenant.js (isFeatureEnabled method)
   *
   * @param {Object} feature - The feature object from FEATURES constant (e.g., FEATURES.INTERCOM)
   * @returns {boolean} true if the feature is enabled; false otherwise
   */
  isFeatureEnabled: (feature) => {
    validateFeatureName(feature)

    const { features } = get()

    // If features not loaded or specific feature not configured, use default
    if (!features || !features[feature.name]) {
      return feature.defaultValue
    }

    const featureFlagType = typeof features[feature.name].enabled

    // Boolean flag: return directly
    if (featureFlagType === 'boolean') {
      return features[feature.name].enabled
    }

    // Object/Array flag: return true if non-empty
    if (featureFlagType === 'object') {
      const enabledValue = features[feature.name].enabled

      // Handle arrays
      if (Array.isArray(enabledValue)) {
        return !!enabledValue.length
      }

      // Handle objects
      return !!Object.keys(enabledValue).length
    }

    // Fallback to default if type is unexpected
    return feature.defaultValue
  },
}))
